import { useCallback, useState } from "react";
import { JobStatus } from "../pages/playground/types";
import { clientBulkApproveJobs } from "../api/client-bulk";

interface ClientBulkApproveResult {
  successful_updates: Array<{
    job_id: string;
    old_status: string;
    new_status: string;
    master_id: string;
  }>;
  failed_updates: Array<{
    job_id: string;
    error: string;
    reason: string;
  }>;
  summary: {
    total_requested: number;
    successful: number;
    failed: number;
  };
}

interface UseClientBulkActionsProps {
  onRefresh: () => Promise<void>;
}

export const useClientBulkActions = ({ onRefresh }: UseClientBulkActionsProps) => {
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState<ClientBulkApproveResult | null>(null);
  const [bulkResultsDialogOpen, setBulkResultsDialogOpen] = useState(false);

  const handleJobSelectionChange = useCallback((jobId: string, selected: boolean) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(jobId);
      } else {
        newSet.delete(jobId);
      }
      return newSet;
    });
  }, []);

  // Filter jobs that can be selected (only DELIVERED jobs)
  const canSelectJob = useCallback((job: { status: JobStatus }) => {
    return job.status === JobStatus.DELIVERED;
  }, []);

  const handleSelectAll = useCallback((selected: boolean, allJobs: Array<{ id: string; status: JobStatus }>) => {
    if (selected) {
      // Only select jobs that can be approved (DELIVERED)
      const selectableJobIds = allJobs
        .filter(job => canSelectJob(job))
        .map(job => job.id);
      setSelectedJobs(new Set(selectableJobIds));
    } else {
      setSelectedJobs(new Set());
    }
  }, [canSelectJob]);

  const handleClearSelection = useCallback(() => {
    setSelectedJobs(new Set());
  }, []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedJobs.size === 0) return;

    setBulkLoading(true);
    try {
      const response = await clientBulkApproveJobs({
        job_ids: Array.from(selectedJobs),
      });

      setBulkResults(response.data.data);
      setBulkResultsDialogOpen(true);
      setSelectedJobs(new Set());
      
      // Refresh job list
      await onRefresh();
    } catch (error) {
      console.error('Client bulk approve failed:', error);
      // You could show an error dialog here
    } finally {
      setBulkLoading(false);
    }
  }, [selectedJobs, onRefresh]);

  const toggleBulkActions = useCallback(() => {
    setShowBulkActions(prev => !prev);
    setSelectedJobs(new Set()); // Clear selection when toggling
  }, []);

  const closeBulkResultsDialog = useCallback(() => {
    setBulkResultsDialogOpen(false);
  }, []);

  return {
    // State
    showBulkActions,
    selectedJobs,
    bulkLoading,
    bulkResults,
    bulkResultsDialogOpen,
    
    // Utilities
    canSelectJob,
    
    // Actions
    handleJobSelectionChange,
    handleSelectAll,
    handleClearSelection,
    handleBulkApprove,
    toggleBulkActions,
    closeBulkResultsDialog,
  };
};